/**
 * Shared primitives for both frontends.
 *
 * Source-only: consumed through a Vite alias, not built or published. Two apps
 * in one repo do not need a package pipeline, and adding one would mean a build
 * step between editing a button and seeing it.
 *
 * What lives here is what the two apps must agree on -- state vocabulary above
 * all. If the captain sees "Approved" green and the admin sees "Godkjent" amber
 * for the same row, the two of them are on the phone about different things.
 */

import type { CSSProperties, ReactNode } from 'react'

/**
 * Coordinates live in ./position -- formatting and parsing together, because
 * they are the same knowledge and a second copy of either is a second set of
 * off-by-one-degree bugs. Re-exported here so `@abg/ui` stays the one import
 * a page needs.
 */
export {
  COORD_FORMATS,
  coordFormatInfo,
  defaultHemisphere,
  distanceM,
  formatLat,
  formatLon,
  formatPosition,
  isNorwegianWaters,
  parseAxis,
  scanForPositions,
  toRunTogether,
  withDefaultHemisphere,
} from './position'
export type {
  Axis,
  CoordFormat,
  CoordFormatInfo,
  Hemisphere,
  ParseResult,
  PositionCandidate,
} from './position'

export type ApplicationState =
  | 'draft' | 'submitted' | 'with_csc' | 'alternative_offered' | 'approved' | 'rejected'
  | 'withdrawn' | 'active' | 'completed' | 'expired' | 'cancelled'

/**
 * Captain-facing copy is English, admin-facing is Norwegian. Not an oversight:
 * the captains here are overwhelmingly not Norwegian speakers, and ABG's
 * internal surfaces follow vessel-map's Norwegian convention. Both dictionaries
 * live together so a new state cannot be added to one and forgotten in the other.
 *
 * **These are ABG's own words**, given 2026-09-02: Posted, For review by CSC,
 * Approved, Rejected, Withdrawn, Anchored, Anchor aweigh. ("For review by
 * agent" was on that first list and ABG then removed it -- it said nothing to a
 * captain that "Posted" did not, and `claimed_by` already records which agent
 * has an application open. Claiming is metadata, not a status.)
 * The database column keeps its original values -- renaming `active` to
 * `anchored` in Postgres would touch every query and index predicate to change
 * a label that lives right here. Display names are a UI concern.
 *
 * `draft`, `expired` and `cancelled` are not among the eight. Draft is an
 * application not yet posted (the PWA lets a captain fill the form with no
 * signal); the other two are the spec's automatic expiry and ABG's ability to
 * pull a granted approval.
 *
 * `alternative_offered` is the ninth, added 2026-09-02: CSC declined the
 * position but named another nearby, and the application is waiting on the
 * captain. The English label says *whose move it is* rather than what happened
 * -- "Position declined" would be accurate and would also read as the end of
 * the matter, when in fact nothing proceeds until the captain answers.
 */
export const STATE_LABEL_EN: Record<ApplicationState, string> = {
  draft: 'Draft',
  submitted: 'Posted',
  with_csc: 'For review by CSC',
  alternative_offered: 'Your answer needed',
  approved: 'Approved',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  active: 'Anchored',
  completed: 'Anchor aweigh',
  expired: 'Expired',
  cancelled: 'Cancelled',
}

export const STATE_LABEL_NO: Record<ApplicationState, string> = {
  draft: 'Utkast',
  submitted: 'Postet',
  with_csc: 'Til behandling hos CSC',
  alternative_offered: 'Venter på kaptein',
  approved: 'Godkjent',
  rejected: 'Avslått',
  withdrawn: 'Trukket',
  active: 'Til ankers',
  completed: 'Anker lettet',
  expired: 'Utløpt',
  cancelled: 'Kansellert',
}

export function StateBadge({
  state,
  lang = 'en',
}: {
  state: ApplicationState
  lang?: 'en' | 'no'
}) {
  const labels = lang === 'no' ? STATE_LABEL_NO : STATE_LABEL_EN
  const style: CSSProperties = {
    color: `var(--state-${state})`,
    background: `var(--state-${state}-wash)`,
  }
  return (
    <span className="badge" style={style}>
      {labels[state] ?? state}
    </span>
  )
}

export function Card({
  title,
  actions,
  children,
  tight,
}: {
  title?: ReactNode
  actions?: ReactNode
  children: ReactNode
  tight?: boolean
}) {
  return (
    <section className={tight ? 'card card-tight' : 'card'}>
      {(title || actions) && (
        <header className="row-between" style={{ marginBottom: 'var(--space-3)' }}>
          {typeof title === 'string' ? <h3 style={{ font: 'var(--text-h3)' }}>{title}</h3> : title}
          {actions}
        </header>
      )}
      {children}
    </section>
  )
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string
  hint?: ReactNode
  error?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint && !error && <span className="hint">{hint}</span>}
      {error && <span className="error">{error}</span>}
    </div>
  )
}

export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'alert' | 'ok'
  children: ReactNode
}) {
  const cls = tone === 'info' ? 'notice' : `notice notice-${tone}`
  return <div className={cls}>{children}</div>
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="row" role="status">
      <span className="spinner" aria-hidden="true" />
      {label && <span className="muted small">{label}</span>}
    </span>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="muted" style={{ padding: 'var(--space-5)', textAlign: 'center' }}>
      {children}
    </p>
  )
}


/** Metres, rounded the way a person would say it. */
export function formatDistance(metres: number): string {
  if (metres < 1000) return `${Math.round(metres)} m`
  return `${(metres / 1000).toFixed(metres < 10000 ? 1 : 0)} km`
}

export function formatLoa(loa: number | string | null | undefined): string {
  if (loa === null || loa === undefined) return '—'
  const n = typeof loa === 'string' ? Number.parseFloat(loa) : loa
  return Number.isFinite(n) ? `${n.toFixed(n % 1 === 0 ? 0 : 1)} m` : '—'
}
